import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Row, Col, Card, Statistic, Space, Divider, Tag, message, Carousel } from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import {
  ArrowRightOutlined,
  BarChartOutlined,
  SafetyOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  StarOutlined,
  DollarOutlined,
  LineChartOutlined,
  GlobalOutlined,
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import LandingLayout from './LandingLayoutRedesign';
import BondTypesSection from './BondTypesSectionRedesign';
import AnimatedVennDiagram from './AnimatedVennDiagramRedesign';
import AnimatedYieldCurveChart from './AnimatedYieldCurveChartRedesign';
import './HomePageRedesign.css';
import apiClient from '../../services/apiClient';

const { Title, Paragraph } = Typography;

const HomePageRedesign = () => {
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [issuersData, setIssuersData] = useState([]);
  const [isinsData, setIsinsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Fetch issuers data
  const fetchIssuersData = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/master-issuer/all`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      // Handle the new API response structure
      if (result.statusCode === 200 && result.data && result.data.content && Array.isArray(result.data.content)) {
        // Show only first 7 issuers from the content array
        setIssuersData(result.data.content.slice(0, 7));
        console.log('Issuers API Response:', result);
      } else if (Array.isArray(result)) {
        // Fallback for old structure
        setIssuersData(result.slice(0, 7));
        console.log('Issuers API Response (fallback):', result);
      } else {
        console.error('Invalid issuers API response structure:', result);
        setIssuersData([]);
      }
    } catch (error) {
      console.error('Error fetching issuers data:', error);
      message.error('Failed to fetch issuers data');
      // Fallback to empty array
      setIssuersData([]);
    }
  };

  // Fetch ISINs data
  const fetchIsinsData = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/master-isin/all`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      // Handle the new API response structure
      if (result.statusCode === 200 && result.data && result.data.content && Array.isArray(result.data.content)) {
        // Show only first 7 ISINs from the content array
        setIsinsData(result.data.content.slice(0, 7));
        console.log('ISINs API Response:', result);
      } else if (Array.isArray(result)) {
        // Fallback for old structure
        setIsinsData(result.slice(0, 7));
        console.log('ISINs API Response (fallback):', result);
      } else {
        console.error('Invalid ISINs API response structure:', result);
        setIsinsData([]);
      }
    } catch (error) {
      console.error('Error fetching ISINs data:', error);
      message.error('Failed to fetch ISINs data');
      // Fallback to empty array
      setIsinsData([]);
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([fetchIssuersData(), fetchIsinsData()]);
      setLoading(false);
    };
    fetchAllData();
  }, []);

  // Helper function to get issuer logo/icon
  const getIssuerLogo = (issuer, industry) => {
    const name = issuer.split(' ')[0].toUpperCase();

    // Industry-based icons
    if (industry === 'Banks') return '🏦';
    if (industry === 'Insurance') return '🛡️';
    if (industry === 'Finance') return '💼';
    if (industry === 'Auto Components') return '🚗';

    // Default to first letter
    return name.charAt(0);
  };

  // Helper function to get issuer short name
  const getIssuerShortName = (issuer) => {
    // Return full name without truncation
    return issuer;
  };

  // Helper function to get ISIN logo/icon
  const getIsinLogo = (isin, industry) => {
    // Industry-based icons
    if (industry === 'Finance') return '💼';
    if (industry === 'Fertilizers & Agrochemicals') return '🌱';
    if (industry === 'Utilities') return '⚡';

    // Default to first 3 characters of ISIN
    return isin.substring(0, 3);
  };

  // Helper function to get ISIN short name
  const getIsinShortName = (isin) => {
    // Return full ISIN without truncation
    return isin;
  };

  // Helper function to get category color for issuers
  const getCategoryColor = (industry) => {
    const colors = {
      'Banks': 'blue',
      'Insurance': 'green',
      'Finance': 'purple',
      'Auto Components': 'orange',
      'Non-Banking Financial Company (NBFC)': 'cyan',
      'Life Insurance': 'green',
      'Public Sector Bank': 'blue',
      'Private Sector Bank': 'blue',
      'General Insurance': 'green',
      'Tyres & Rubber Products': 'orange',
      'Financial Services': 'purple',
      'Consumer Discretionary': 'orange',
      'Automobile and Auto Components': 'orange'
    };
    return colors[industry] || 'default';
  };

  // Helper function to get ISIN category color
  const getIsinCategoryColor = (industry) => {
    const colors = {
      'Finance': 'blue',
      'Fertilizers & Agrochemicals': 'green',
      'Utilities': 'orange',
      'Non-Banking Financial Company (NBFC)': 'cyan',
      'Fertilizers': 'green',
      'Life Insurance': 'green',
      'General Insurance': 'green'
    };
    return colors[industry] || 'default';
  };

  // Click handler for issuer cards
  const handleIssuerClick = (issuer) => {
    navigate(`/issuer-details/${issuer.corporateIdentityNumber}`);
  };

  // Click handler for ISIN cards
  const handleIsinClick = (isin) => {
    console.log('ISIN clicked:', isin);
    navigate(`/isin-details/${isin.isin}`);
  };
  return (
    <LandingLayout>
      <div className={`homepage ${isDark ? "dark" : ""}`}>
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-bg-blobs">
            <div className="hero-blob hero-blob-1"></div>
            <div className="hero-blob hero-blob-2"></div>
          </div>
          <div className="hero-container">
            <Row gutter={[48, 48]} align="middle">
              <Col xs={30} lg={14}>
                <div className="hero-content">
                  <motion.div 
                    className="hero-badge"
                    initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                    whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  >
                    <StarOutlined />
                    <span>Bond with the Best</span>
                  </motion.div>
                  <Title level={1} className="hero-title">
                    {["Credible", "Ecosystem", "providing", "Comprehensive", "Fixed", "Income", "Solutions"].map((word, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                        whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        viewport={{ once: true }}
                        transition={{ 
                          duration: 0.8, 
                          delay: 0.2 + (i * 0.08),
                          ease: [0.21, 1.11, 0.81, 0.99]
                        }}
                        style={{ marginRight: '0.25em' }}
                      >
                        {word}
                      </motion.span>
                    ))}
                  </Title>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
                  >
                    <Paragraph className="hero-subtitle">
                      Comprehensive bond market insights, real-time analytics, and expert research
                      to help you make informed fixed income investment decisions.
                    </Paragraph>
                  </motion.div>
                </div>
              </Col>
              <Col xs={18} lg={10}>
                <div className="venn-diagram-section">
                  <AnimatedVennDiagram />
                </div>


                {/* <div className="hero-visual">
                  <div className="yield-charts-container">
                    <div className="chart-card">
                      <div className="chart-header">
                        <div className="chart-title">
                          <LineChartOutlined />
                          <span>Corporate Bond Yields</span>
                        </div>
                        <div className="chart-period">Last 30 Days</div>
                      </div>
                      <div className="yield-chart">
                        <div className="chart-line">
                          <div className="chart-point" style={{ left: '10%', top: '20%' }}></div>
                          <div className="chart-point" style={{ left: '25%', top: '35%' }}></div>
                          <div className="chart-point" style={{ left: '40%', top: '25%' }}></div>
                          <div className="chart-point" style={{ left: '55%', top: '45%' }}></div>
                          <div className="chart-point" style={{ left: '70%', top: '30%' }}></div>
                          <div className="chart-point" style={{ left: '85%', top: '40%' }}></div>
                          <div className="chart-point" style={{ left: '95%', top: '35%' }}></div>
                        </div>
                        <div className="chart-labels">
                          <span>6.5%</span>
                          <span>7.0%</span>
                          <span>7.5%</span>
                          <span>8.0%</span>
                        </div>
                      </div>
                    </div>

                    <div className="chart-card secondary">
                      <div className="chart-header">
                        <div className="chart-title">
                          <BarChartOutlined />
                          <span>Government Securities</span>
                        </div>
                        <div className="chart-period">Current Rates</div>
                      </div>
                      <div className="bar-chart">
                        <div className="bar-item">
                          <div className="bar" style={{ height: '60%' }}></div>
                          <span>3M</span>
                        </div>
                        <div className="bar-item">
                          <div className="bar" style={{ height: '70%' }}></div>
                          <span>6M</span>
                        </div>
                        <div className="bar-item">
                          <div className="bar" style={{ height: '80%' }}></div>
                          <span>1Y</span>
                        </div>
                        <div className="bar-item">
                          <div className="bar" style={{ height: '85%' }}></div>
                          <span>3Y</span>
                        </div>
                        <div className="bar-item">
                          <div className="bar" style={{ height: '90%' }}></div>
                          <span>5Y</span>
                        </div>
                        <div className="bar-item">
                          <div className="bar" style={{ height: '95%' }}></div>
                          <span>10Y</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </Col>
            </Row>
          </div>
        </section>

        <motion.section 
          className="features-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="container">
            <AnimatedYieldCurveChart />
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section 
          className="features-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="container">
            <div className="section-header">
              <Title level={2} className="section-title">
                Why Choose Simply FI
              </Title>
              <Paragraph className="section-subtitle">
                Presenting appropriate risk-return opportunities ensuring financial discipline.</Paragraph>
            </div>

            {/* Desktop View */}
            <div className="desktop-features">
              <Row gutter={[16, 16]}>
                {[
                  { icon: <BarChartOutlined />, title: "Appropriateness with the Risk Appetite", text: "Portfolio construction to meet financial objectives. Diversification of risks for building stable returns" },
                  { icon: <SafetyOutlined />, title: "Risk Based Returns", text: "Deep insights into bond performances and market yields. Advanced analytics tools processing real time data" },
                  { icon: <RocketOutlined />, title: "Comprehensive Risk Assessment", text: "Framework based on extensive domain expertise. Tracking market volatility & Assessment of Default Risks" }
                ].map((feature, i) => (
                  <Col xs={24} sm={24} md={8} key={i}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.2 }}
                    >
                      <Card className="feature-card">
                        <div className="feature-icon">
                          {feature.icon}
                        </div>
                        <Title level={4}>{feature.title}</Title>
                        <Paragraph>{feature.text}</Paragraph>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Mobile Carousel View */}
            <div className="mobile-features">
              <Carousel
                dots={{ position: 'bottom' }}
                autoplay={true}
                autoplaySpeed={3000}
                infinite={true}
                slidesToShow={1}
                slidesToScroll={1}
                responsive={[
                  {
                    breakpoint: 768,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      dots: true,
                      arrows: true,
                      adaptiveHeight: true,
                      autoplay: true,
                      autoplaySpeed: 3000
                    }
                  }
                ]}
                adaptiveHeight={true}
                className="features-carousel"
              >
                <div className="carousel-slide">
                  <Card className="feature-card">
                    <div className="feature-icon">
                      <BarChartOutlined />
                    </div>
                    <Title level={4}>Appropriateness with the Risk Appetite</Title>
                    <Paragraph>
                      Portfolio construction to meet financial objectives
                      Diversification of risks for building stable returns
                    </Paragraph>
                  </Card>
                </div>

                <div className="carousel-slide">
                  <Card className="feature-card">
                    <div className="feature-icon">
                      <SafetyOutlined />
                    </div>
                    <Title level={4}>Risk Based Returns</Title>
                    <Paragraph>
                      Deep insights into bond performances and market yields
                      Advanced analytics tools processing real time data
                    </Paragraph>
                  </Card>
                </div>

                <div className="carousel-slide">
                  <Card className="feature-card">
                    <div className="feature-icon">
                      <RocketOutlined />
                    </div>
                    <Title level={4}>Comprehensive Risk Assessment</Title>
                    <Paragraph>
                      Framework based on extensive domain expertise
                      Tracking market volatility & Assessment of Default Risks
                    </Paragraph>
                  </Card>
                </div>
              </Carousel>
            </div>
          </div>
        </motion.section>

        <BondTypesSection />

        {/* Issuers Section */}
        <motion.section 
          className="explore-section issuers-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="container">
            <div className="explore-header">
              <Title level={2} className="explore-title">
                Explore by Issuers
              </Title>
              <Paragraph className="explore-subtitle">
                Discover top bond issuers across various industries
              </Paragraph>
            </div>

            <div className="explore-grid">
              {loading ? (
                // Loading state
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="explore-card loading">
                    <div className="explore-card-icon">...</div>
                    <div className="explore-card-content">
                      <div className="explore-card-title">Loading...</div>
                      <div className="explore-card-subtitle">Loading...</div>
                    </div>
                  </div>
                ))
              ) : (
                // Real data from API
                issuersData.slice(0, 5).map((issuer, index) => {
                  const logo = getIssuerLogo(issuer.issuer, issuer.industry);
                  const shortName = getIssuerShortName(issuer.issuer);
                  const isIcon = ['🏦', '🛡️', '💼', '🚗'].includes(logo);

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <div
                        className="explore-card"
                        onClick={() => handleIssuerClick(issuer)}
                      >
                        <div className="explore-card-icon">
                          {isIcon ? (
                            <span className="icon-emoji">{logo}</span>
                          ) : (
                            <span className="icon-text">{logo}</span>
                          )}
                        </div>
                        <div className="explore-card-content">
                          <div className="explore-card-title">{shortName}</div>
                          <div className="explore-card-subtitle">
                            {issuer.industry || issuer.basicIndustry || 'N/A'}
                          </div>
                        </div>
                        <div className="explore-card-arrow">
                          <ArrowRightOutlined />
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}

              {/* View All Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div
                  className="explore-card view-all-card"
                  onClick={() => navigate('/issuers')}
                >
                  <div className="explore-card-icon view-all-icon">
                    <ArrowRightOutlined />
                  </div>
                  <div className="explore-card-content">
                    <div className="explore-card-title">View All Issuers</div>
                    <div className="explore-card-subtitle">Browse complete list</div>
                  </div>
                  <div className="explore-card-arrow">
                    <ArrowRightOutlined />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>



        {/* ISINs Section */}
        <motion.section 
          className="explore-section isins-section" 
          style={{ marginTop: '100px', paddingTop: '50px' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="container">
            <div className="explore-header">
              <Title level={2} className="explore-title">
                Explore by Instruments
              </Title>
              <Paragraph className="explore-subtitle">
                Browse individual bond securities and their details
              </Paragraph>
            </div>

            <div className="explore-grid">
              {loading ? (
                // Loading state
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="explore-card loading">
                    <div className="explore-card-icon">...</div>
                    <div className="explore-card-content">
                      <div className="explore-card-title">Loading...</div>
                      <div className="explore-card-subtitle">Loading...</div>
                    </div>
                  </div>
                ))
              ) : (
                // Real data from API
                isinsData.slice(0, 5).map((isin, index) => {
                  const logo = getIsinLogo(isin.isin, isin.industry);
                  const shortName = getIsinShortName(isin.isin);
                  const isIcon = ['💼', '🌱', '⚡'].includes(logo);

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <div
                        className="explore-card"
                        onClick={() => handleIsinClick(isin)}
                      >
                        <div className="explore-card-icon">
                          {isIcon ? (
                            <span className="icon-emoji">{logo}</span>
                          ) : (
                            <span className="icon-text">{logo}</span>
                          )}
                        </div>
                        <div className="explore-card-content">
                          <div className="explore-card-title">{shortName}</div>
                          <div className="explore-card-subtitle">
                            {isin.industry || isin.basicIndustry || 'N/A'}
                          </div>
                        </div>
                        <div className="explore-card-arrow">
                          <ArrowRightOutlined />
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}

              {/* View All Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div
                  className="explore-card view-all-card"
                  onClick={() => navigate('/isins')}
                >
                  <div className="explore-card-icon view-all-icon">
                    <ArrowRightOutlined />
                  </div>
                  <div className="explore-card-content">
                    <div className="explore-card-title">View All ISINs</div>
                    <div className="explore-card-subtitle">Browse complete list</div>
                  </div>
                  <div className="explore-card-arrow">
                    <ArrowRightOutlined />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>




      </div>
    </LandingLayout>
  );
};

export default HomePageRedesign;
